import { useAuthStore } from '~/stores/auth';
import type { APIErrorResponse, JsonApiResponse } from '~/interfaces/api/main';

export async function getAuthHeaders (): Promise<Record<string, string>> {
  const authStore = useAuthStore();
  if (!authStore.authCookie) {
    await authStore.fetchToken();
  }
  return authStore.authCookie
    ? { Authorization: `Bearer ${authStore.authCookie}` }
    : {};
}

/**
 * Set by the hosting ARC analysis sheet, which hands over the token it held when it loaded.
 * Read from the location, not the router, so a store action can call it outside any setup.
 */
export function getArcSheetHeaders (): Record<string, string> {
  if (!import.meta.client) {
    return {};
  }

  const fingerprint = new URLSearchParams(window.location.search).get('fingerprint');

  return fingerprint ? { 'X-OPPORTUNITY-FINGERPRINT': fingerprint } : {};
}

interface ProblemViolation {
  propertyPath?: string;
  title?: string;
}

interface Problem {
  title?: string;
  detail?: string;
  status?: number;
  violations?: ProblemViolation[];
}

export function normalizeApiError (
  errorData: unknown,
  statusCode: number,
  transactionId: string | null,
  detail?: string,
): APIErrorResponse {
  if (isAPIErrorResponse(errorData)) {
    return {
      transactionId,
      errors: (errorData as APIErrorResponse).errors,
      statusCode,
    };
  }
  const problem: Problem = (errorData && typeof errorData === 'object') ? errorData as Problem : {};
  const status = problem.status ?? statusCode;
  const refusals = (Array.isArray(problem.violations) ? problem.violations : [])
    .filter(violation => typeof violation?.title === 'string' && violation.title !== '');
  if (refusals.length > 0) {
    return {
      transactionId,
      errors: refusals.map(violation => ({
        title: violation.title,
        detail: violation.title,
        status,
        source: { pointer: violation.propertyPath ?? null },
      })),
      statusCode,
    };
  }
  return {
    transactionId,
    errors: [{
      title: problem.title ?? 'L\'API Goldorac a rencontré une erreur',
      detail: problem.detail ?? detail ?? 'Unknown error',
      status,
    }],
    statusCode,
  };
}

export function normalizeJsonApiResponse<T = any> (data: any): JsonApiResponse<T> {
  return {
    data: data?.data,
    links: data?.links ?? {},
    meta: data?.meta ?? {},
  };
}
